import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Building2, Hospital, User } from "lucide-react";
import { toast } from "sonner";

const BG = process.env.REACT_APP_AUTH_BG_IMAGE;

const ROLES = [
  { value: "gerant", label: "Gérant de société", icon: Building2, desc: "Transport sanitaire & taxi" },
  { value: "etablissement", label: "Établissement de santé", icon: Hospital, desc: "Hôpital, clinique, SSR" },
  { value: "patient", label: "Patient / Usager", icon: User, desc: "Réservation & suivi" },
];

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [role, setRole] = useState("gerant");
  const [form, setForm] = useState({ name: "", company_name: "", etablissement_name: "", etablissement_type: "Hôpital", phone: "", email: "", password: "", referral_code: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register({ ...form, role });
      toast.success("Compte créé. Bienvenue sur PACEMIA !");
      navigate("/");
    } catch (err) {
      toast.error(apiError(err, "Inscription impossible"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:block relative order-last lg:order-first">
        <img src={BG} alt="Flotte" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-sky-900/90 via-sky-800/40 to-transparent" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <p className="font-display text-3xl font-bold leading-tight">Un écosystème, trois métiers.</p>
          <p className="text-white/80 mt-2">Gérants, établissements et patients connectés en temps réel.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-8 overflow-auto">
        <div className="w-full max-w-sm py-6">
          <Logo size={32} className="mb-6" />
          <h1 className="font-display text-2xl font-bold tracking-tight">Créer un compte</h1>

          <div className="grid grid-cols-3 gap-2 mt-4 mb-5">
            {ROLES.map((r) => (
              <button
                key={r.value}
                type="button"
                onClick={() => setRole(r.value)}
                data-testid={`role-${r.value}`}
                className={`flex flex-col items-center gap-1 p-2.5 rounded-md border text-center transition-colors ${role === r.value ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:bg-secondary"}`}
              >
                <r.icon className="h-5 w-5" />
                <span className="text-[10px] font-medium leading-tight">{r.label}</span>
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="space-y-3.5">
            <div>
              <Label htmlFor="name">{role === "etablissement" ? "Nom du contact" : "Votre nom"}</Label>
              <Input id="name" value={form.name} onChange={set("name")} data-testid="register-name" required className="mt-1.5" />
            </div>

            {role === "gerant" && (
              <div>
                <Label htmlFor="company">Nom de la société</Label>
                <Input id="company" value={form.company_name} onChange={set("company_name")} data-testid="register-company" required className="mt-1.5" />
              </div>
            )}
            {role === "etablissement" && (
              <>
                <div>
                  <Label htmlFor="etab">Nom de l'établissement</Label>
                  <Input id="etab" value={form.etablissement_name} onChange={set("etablissement_name")} data-testid="register-etab-name" required className="mt-1.5" />
                </div>
                <div>
                  <Label>Type</Label>
                  <Select value={form.etablissement_type} onValueChange={(v) => setForm({ ...form, etablissement_type: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="register-etab-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Hôpital">Hôpital public</SelectItem>
                      <SelectItem value="Clinique">Clinique privée</SelectItem>
                      <SelectItem value="SSR">SSR</SelectItem>
                      <SelectItem value="Rééducation">Centre de rééducation</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
            {role === "patient" && (
              <div>
                <Label htmlFor="phone">Téléphone</Label>
                <Input id="phone" value={form.phone} onChange={set("phone")} data-testid="register-phone" className="mt-1.5" />
              </div>
            )}

            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={form.email} onChange={set("email")} data-testid="register-email" required className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="password">Mot de passe</Label>
              <Input id="password" type="password" value={form.password} onChange={set("password")} data-testid="register-password" required className="mt-1.5" />
            </div>
            {role === "gerant" && (
              <div>
                <Label htmlFor="ref">Code parrainage (optionnel)</Label>
                <Input id="ref" value={form.referral_code} onChange={set("referral_code")} data-testid="register-referral" placeholder="PCM-XXXXXX" className="mt-1.5" />
              </div>
            )}
            <Button type="submit" className="w-full" disabled={loading} data-testid="register-submit">
              {loading ? "Création…" : role === "gerant" ? "Démarrer l'essai gratuit" : "Créer mon compte gratuit"}
            </Button>
          </form>
          <p className="text-sm text-muted-foreground mt-5">
            Déjà inscrit ?{" "}
            <Link to="/login" className="text-primary font-medium hover:underline" data-testid="link-login">Se connecter</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
